import { ArrowUpTrayIcon } from "@heroicons/react/24/outline";
import { ReactElement, useRef } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from "react-hook-form";

import { ErrorMessage } from "../catalyst/ErrorMessage";
import { Field } from "../catalyst/Field";
import { Label } from "../catalyst/Label";
import { Text } from "../catalyst/Text";

interface FileUploadProps<T extends FieldValues> {
  acceptedFileTypes?: string;
  className?: string;
  control: Control<T>;
  description?: string;
  errors: FieldErrors<T>;
  fieldName?: Path<T>;
  label?: string;
  maxFileSize?: number;
  maxRows?: null | number;
  onFileSelect: (file: File) => void;
  title?: string;
}

export const FileUpload = <T extends FieldValues>({
  acceptedFileTypes = ".csv",
  className = "",
  control,
  description = "Select a CSV file to import into your Workspace.",
  errors,
  fieldName = "csvFile" as Path<T>,
  label = "Select CSV File",
  maxFileSize = 10 * 1024 * 1024, // 10MB
  maxRows = null,
  onFileSelect,
  title = "Upload CSV File",
}: FileUploadProps<T>): ReactElement => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void,
  ): void => {
    const file = event.target.files?.[0];
    if (file) {
      onChange(file.name);
      onFileSelect(file);
    }
  };

  const handleBrowseClick = (): void => {
    fileInputRef.current?.click();
  };

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb}MB`;
  };

  const getFileTypesText = (): string => {
    if (!acceptedFileTypes) return "files";
    const types = acceptedFileTypes
      .split(",")
      .map((t) => t.trim().toUpperCase().replace(".", ""))
      .join(", ");
    return `${types} files`;
  };

  const getFileSizeText = (): string => {
    const sizeText = `MAX. ${formatFileSize(maxFileSize)}`;
    if (maxRows) {
      return `${sizeText}, ${maxRows} rows`;
    }
    return sizeText;
  };

  return (
    <div className="space-y-4">
      {(title || description) && (
        <div>
          {title && <h3 className="text-lg font-medium text-white">{title}</h3>}
          {description && <p className="text-gray-300">{description}</p>}
        </div>
      )}

      <Controller
        control={control}
        name={fieldName}
        render={({ field: { onChange, value } }) => (
          <Field className={className}>
            <Label>{label}</Label>
            <div className="mt-2">
              <input
                accept={acceptedFileTypes}
                className="hidden"
                onChange={(e) => handleFileSelect(e, onChange)}
                ref={fileInputRef}
                type="file"
              />
              <div className="flex w-full items-center justify-center">
                <label
                  className="flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-zinc-300 bg-zinc-50 hover:bg-zinc-100 dark:border-zinc-600 dark:bg-zinc-700 dark:hover:bg-zinc-600"
                  onClick={handleBrowseClick}
                >
                  <div className="flex flex-col items-center justify-center pb-6 pt-5">
                    <ArrowUpTrayIcon className="mb-4 h-8 w-8 text-zinc-500" />
                    <p className="mb-2 text-sm text-zinc-500 dark:text-zinc-400">
                      <span className="font-semibold">Click to browse</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      {getFileTypesText()} ({getFileSizeText()})
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {errors[fieldName] && (
              <div className="mt-3">
                <ErrorMessage>
                  {(errors[fieldName]?.message as string) || "Invalid file"}
                </ErrorMessage>
              </div>
            )}

            {value && (
              <div className="mt-3">
                <Text className="text-sm text-zinc-400">
                  Selected file: {value}
                </Text>
              </div>
            )}
          </Field>
        )}
        rules={{
          required: `Please select a file to upload`,
        }}
      />
    </div>
  );
};
